import { useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  AiChatsQuery,
  OnAiChatResponseSubscription,
  OnAiChatResponseSubscriptionVariables,
} from "lib/generated/app-service-gql/graphql";
import { useSubscription, UseSubscriptionOptions } from "../useGraphQlClient";
import { AiConversation, QaContent } from "./useGetAiChats";

// adding this so it generates the types
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _onAiChatResponse = graphql(`
  subscription onAiChatResponse($conversationId: String!) {
    onAiChatResponse(conversationId: $conversationId) {
      conversationId
      resourceId
      resourceType
      question
      answer
      sources {
        id
        type
        title
        url
        chunkNumber
        locations {
          pageNumber
          bbox {
            x
            y
            width
            height
          }
        }
        documentType
      }
      isComplete
    }
  }
`);

export function useAiChatStreamSubscription(
  conversationId: string | undefined,
  options: UseSubscriptionOptions<OnAiChatResponseSubscription>,
) {
  const client = useQueryClient();

  const { data, ...rest } = useSubscription<
    OnAiChatResponseSubscription,
    OnAiChatResponseSubscriptionVariables
  >(
    `
  subscription onAiChatResponse($conversationId: String!) {
    onAiChatResponse(conversationId: $conversationId) {
      conversationId
      resourceId
      resourceType
      question
      answer
      sources {
        id
        type
        title
        url
        chunkNumber
        locations {
          pageNumber
          bbox {
            x
            y
            width
            height
          }
        }
        documentType
      }
      isComplete
    }
  }
  `,
    { conversationId: conversationId || "" },
    {
      ...options,

      // Updating the previously fetched conversations that the user has had on
      // with the new question and answer
      onData: (data) => {
        // Trigger the onData callback from the caller
        options.onData?.(data);

        // Getting data from subscription response
        const { conversationId, resourceId, resourceType, question, answer, sources, isComplete } =
          data.onAiChatResponse;

        client.setQueryData(
          ["get_ai_chats", { resourceId, resourceType }],
          (oldData: AiChatsQuery | undefined) => {
            // Only update cache when subscription is complete
            if (!oldData || !isComplete) {
              return oldData;
            }

            const newQaContent: QaContent = {
              question,
              answer: answer ?? "",
              sources,
            };

            // Try to find the existing conversation
            const existingConv = oldData.aiChats.find(
              (prevData) => prevData.conversationId === conversationId,
            );

            let updatedConversation: AiConversation;
            if (existingConv) {
              // Append to the existing conversation
              updatedConversation = {
                ...existingConv,
                questionAndAnswer: [...existingConv.questionAndAnswer, newQaContent],
              };
            } else {
              // Create new conversation
              updatedConversation = {
                conversationId,
                resourceId,
                questionAndAnswer: [newQaContent],
              };
            }

            // Rebuild array with updated conversation at the top, removing duplicates
            const updatedData: AiConversation[] = [
              updatedConversation,
              ...oldData.aiChats.filter((c) => c.conversationId !== conversationId),
            ];

            return {
              aiChats: updatedData,
            };
          },
        );
      },
    },
  );

  return { data: data?.onAiChatResponse, ...rest };
}
